import React, { useState } from 'react';
import './FlashcardsManage.css';

/**
 * FlashcardsManage - Management tab for deck actions, exports, and settings
 * 
 * Features:
 * - Deck Actions: New, Regenerate, Delete
 * - Export section with dropdown (JSON, CSV, Print)
 * - Study Settings: auto-advance, continue behavior
 * 
 * @param {Object} props
 * @param {Function} props.onNewDeck - Create new deck handler
 * @param {Function} props.onRegenerate - Regenerate deck handler
 * @param {Function} props.onDeleteDeck - Delete deck handler
 * @param {Function} props.onExportJSON - Export JSON handler (markedOnly boolean param)
 * @param {Function} props.onExportCSV - Export CSV handler (markedOnly boolean param)
 * @param {Function} props.onPrint - Print handler
 * @param {number} props.markedCount - Number of marked cards
 * @param {number} props.autoAdvanceSeconds - Current auto-advance setting
 * @param {Function} props.onAutoAdvanceChange - Auto-advance change handler
 * @param {boolean} props.autoAdvanceOnKnown - Auto-advance after marking known
 * @param {Function} props.onAutoAdvanceOnKnownChange - Handler for auto-advance-on-known toggle
 * @param {string} props.continueBehavior - 'resume' | 'nextUnviewed'
 * @param {Function} props.onContinueBehaviorChange - Continue behavior change handler
 * @param {boolean} props.canRegenerate - Whether regenerate is available
 * @param {boolean} props.isExpanded - Whether panel is expanded
 */
export default function FlashcardsManage({
  onNewDeck,
  onRegenerate,
  onDeleteDeck,
  onExportJSON,
  onExportCSV,
  onPrint,
  markedCount = 0,
  autoAdvanceSeconds = 0,
  onAutoAdvanceChange,
  autoAdvanceOnKnown = false,
  onAutoAdvanceOnKnownChange,
  continueBehavior = 'resume',
  onContinueBehaviorChange,
  canRegenerate = false,
  isExpanded = false
}) {
  const [expandedSection, setExpandedSection] = useState('deckActions');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleExportSelect = (type, markedOnly = false) => {
    
    switch (type) {
      case 'json':
        onExportJSON(markedOnly);
        break;
      case 'csv':
        onExportCSV(markedOnly);
        break;
      case 'print':
        onPrint();
        break;
      default:
        break;
    }
  };

  return (
    <div className={`flashcards-manage ${isExpanded ? 'flashcards-manage--expanded' : ''}`}>
      {/* Scout image - only rendered when panel is expanded */}
      {isExpanded && (
        <div className="flashcards-manage__scout">
          <img 
            src="/scout_images/scout_flashcard.png" 
            alt="Scout mascot"
            className="flashcards-manage__scout-image"
          />
        </div>
      )}

      {/* Sections container */}
      <div className="flashcards-manage__sections">
      {/* Deck Actions Section */}
      <div className="flashcards-manage__section">
        <button
          className="flashcards-manage__section-header"
          onClick={() => toggleSection('deckActions')}
          aria-expanded={expandedSection === 'deckActions'}
        >
          <span className="flashcards-manage__section-title">Deck Actions</span>
          <span className="flashcards-manage__section-icon">
            {expandedSection === 'deckActions' ? '▲' : '▼'}
          </span>
        </button>
        {expandedSection === 'deckActions' && (
          <div className="flashcards-manage__section-content">
            <button
              className="ui-btn ui-btn--primary flashcards-manage__action-btn"
              onClick={onNewDeck}
            >
              New Deck
            </button>
            {canRegenerate && (
              <button
                className="ui-btn ui-btn--neutral flashcards-manage__action-btn"
                onClick={onRegenerate}
              >
                Regenerate
              </button>
            )}
            <button
              className="ui-btn ui-btn--danger flashcards-manage__action-btn"
              onClick={onDeleteDeck}
            >
              Delete Deck
            </button>
          </div>
        )}
      </div>

      {/* Export Section */}
      <div className="flashcards-manage__section">
        <button
          className="flashcards-manage__section-header"
          onClick={() => toggleSection('export')}
          aria-expanded={expandedSection === 'export'}
        >
          <span className="flashcards-manage__section-title">Export</span>
          <span className="flashcards-manage__section-icon">
            {expandedSection === 'export' ? '▲' : '▼'}
          </span>
        </button>
        {expandedSection === 'export' && (
          <div className="flashcards-manage__section-content">
            <button
              className="ui-btn ui-btn--neutral flashcards-manage__action-btn"
              onClick={() => handleExportSelect('json', false)}
            >
              Export JSON
            </button>
            <button
              className="ui-btn ui-btn--neutral flashcards-manage__action-btn"
              onClick={() => handleExportSelect('json', true)}
              disabled={markedCount === 0}
            >
              Export Marked JSON
            </button>
            <button
              className="ui-btn ui-btn--neutral flashcards-manage__action-btn"
              onClick={() => handleExportSelect('csv', false)}
            >
              Export CSV
            </button>
            <button
              className="ui-btn ui-btn--neutral flashcards-manage__action-btn"
              onClick={() => handleExportSelect('csv', true)}
              disabled={markedCount === 0}
            >
              Export Marked CSV
            </button>
            <button
              className="ui-btn ui-btn--neutral flashcards-manage__action-btn"
              onClick={() => handleExportSelect('print')}
            >
              Print
            </button>
          </div>
        )}
      </div>

      {/* Study Settings Section */}
      <div className="flashcards-manage__section">
        <button
          className="flashcards-manage__section-header"
          onClick={() => toggleSection('settings')}
          aria-expanded={expandedSection === 'settings'}
        >
          <span className="flashcards-manage__section-title">Study Settings</span>
          <span className="flashcards-manage__section-icon">
            {expandedSection === 'settings' ? '▲' : '▼'}
          </span>
        </button>
        {expandedSection === 'settings' && (
          <div className="flashcards-manage__section-content">
            {/* Auto-advance */}
            <div className="flashcards-manage__setting">
              <label className="flashcards-manage__setting-label">
                Auto-advance after flip:
                <select
                  value={autoAdvanceSeconds}
                  onChange={(e) => onAutoAdvanceChange(parseInt(e.target.value))}
                  className="flashcards-manage__select"
                >
                  <option value="0">Off</option>
                  <option value="3">3 seconds</option>
                  <option value="5">5 seconds</option>
                  <option value="8">8 seconds</option>
                </select>
              </label>
            </div>

            {/* Auto-advance on known */}
            <div className="flashcards-manage__setting">
              <label className="flashcards-manage__setting-label flashcards-manage__setting-label--checkbox">
                <input
                  type="checkbox"
                  checked={autoAdvanceOnKnown}
                  onChange={(e) => onAutoAdvanceOnKnownChange(e.target.checked)}
                  className="flashcards-manage__checkbox"
                />
                <span>Auto-advance after marking Known</span>
              </label>
            </div>

            {/* Continue behavior */}
            <div className="flashcards-manage__setting">
              <label className="flashcards-manage__setting-label">
                Continue button behavior:
                <select
                  value={continueBehavior}
                  onChange={(e) => onContinueBehaviorChange(e.target.value)}
                  className="flashcards-manage__select"
                >
                  <option value="resume">Resume from last position</option>
                  <option value="nextUnviewed">Next unviewed card</option>
                </select>
              </label>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
